import { FC } from "react";
import {
  Avatar,
  AvatarFallback,
} from "@/app/components/shadcn/avatar";
import { Skeleton } from "@/app/components/shadcn/skeleton";
import FallbackImage from "@/app/components/elements/FallbackImage";

interface UserAvatarProps {
  firstname: string;
  lastname: string;
  profilePhotoUrl?: string;
  size?: "sm" | "md" | "lg";
  loading?: boolean;
}

const SIZE_CLASS_MAP: Record<NonNullable<UserAvatarProps["size"]>, string> = {
  sm: "w-6 h-6",
  md: "w-10 h-10",
  lg: "w-16 h-16",
};

const SIZE_PX_MAP: Record<NonNullable<UserAvatarProps["size"]>, string> = {
  sm: "24px",
  md: "40px",
  lg: "64px",
};

const FONT_SIZE_MAP: Record<NonNullable<UserAvatarProps["size"]>, string> = {
  sm: "text-[10px]",
  md: "text-sm",
  lg: "text-lg",
};

const getInitials = (firstname: string, lastname: string): string => {
  const first = firstname.charAt(0).toUpperCase();
  const last = lastname.charAt(0).toUpperCase();
  return `${first}${last}`;
};

export const UserAvatar: FC<UserAvatarProps> = ({
  firstname,
  lastname,
  profilePhotoUrl,
  size = "sm",
  loading = false,
}) => {
  const sizeClass = SIZE_CLASS_MAP[size];

  if (loading) {
    return <Skeleton className={`${sizeClass} rounded-full`} />;
  }

  return (
    <Avatar className={sizeClass}>
      {profilePhotoUrl ? (
        <div className="relative w-full h-full">
          <FallbackImage
            src={profilePhotoUrl}
            alt={`${firstname} ${lastname}`}
            fill
            sizes={SIZE_PX_MAP[size]}
            className="object-cover rounded-full"
          />
        </div>
      ) : (
        <AvatarFallback className={FONT_SIZE_MAP[size]}>
          {getInitials(firstname, lastname)}
        </AvatarFallback>
      )}
    </Avatar>
  );
};
